import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CheckoutForm } from "@/components/CheckoutForm";
import { CurrencyIcon } from "@/components/CurrencyIcon";
import { usePaymentConfig } from "@/hooks/use-payment-config";

// loadStripe must only ever run once per publishable key.
let stripePromise: Promise<Stripe | null> | null = null;
const getStripePromise = (publishableKey: string) => {
  if (!stripePromise) stripePromise = loadStripe(publishableKey);
  return stripePromise;
};

type PaymentButtonProps = {
  onPaid: () => void;
};

export const PaymentButton = ({ onPaid }: PaymentButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { data: config, isLoading, isError, amountLabel, currency } = usePaymentConfig();

  const elementsOptions = useMemo(
    () =>
      config
        ? ({
            mode: 'payment' as const,
            amount: config.amount,
            currency: config.currency,
            paymentMethodTypes: ['card'],
            appearance: {
              theme: 'stripe' as const,
              variables: {
                colorPrimary: '#a855f7',
                borderRadius: '8px',
                fontFamily: 'system-ui, sans-serif',
              },
            },
          })
        : null,
    [config],
  );

  return (
    <>
      <Button
        size="lg"
        disabled={isLoading || isError}
        className="group relative overflow-hidden bg-gradient-to-br from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 transition-all duration-300 animate-bounce-slow"
        onClick={() => setIsOpen(true)}
      >
        <span className="relative z-10 flex items-center gap-2">
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Getting ready…
            </>
          ) : (
            <>
              <CurrencyIcon currency={currency} className="w-5 h-5" />
              Pay {amountLabel} to Find Out
            </>
          )}
        </span>
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Pay {amountLabel} to see the number</DialogTitle>
            <DialogDescription>
              Access lasts 5 minutes. No account, no email — just pay and look.
            </DialogDescription>
          </DialogHeader>
          {config && elementsOptions ? (
            <Elements stripe={getStripePromise(config.publishableKey)} options={elementsOptions}>
              <CheckoutForm
                amountLabel={amountLabel}
                currency={currency}
                onPaid={() => {
                  setIsOpen(false);
                  onPaid();
                }}
              />
            </Elements>
          ) : (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
